import { AppPaths } from '@core/routes/routesPaths';
import { Trans } from '@lingui/macro';
import { AppFeatureFlags, AppPermissions } from '@root/appPermissions';
import { environment } from '@root/environment';
import {
  leakAnalysisFilters,
  maintenanceFilters,
  newLeaksFilters,
  sitesAndLnsFilters,
} from '@shared/components/SiteSharedFilters/constants';
import { IAppLayoutProps } from '../Layout/Layout';
import { styleConstants } from '@styles/constants';
import { useCallback, useMemo, useState } from 'react';
import { Menu, makeStyles } from '@material-ui/core';
import { useAppSelector } from '@core/store/store';
import {
  INavLink,
  usePermission,
  useSetLayoutProps,
  selectUserProfileType,
  FeatureFlagInfoWrapper,
  useFeatureFlag,
} from '@hwm/ui-lib';
import { NavLink, useLocation } from 'react-router-dom';
import { isNil } from 'lodash';

export const Dropdown = {
  Functions: 'Functions',
  LeakManagement: 'LeakManagement',
} as const;
export type DropdownType = (typeof Dropdown)[keyof typeof Dropdown];

//

const getMenu = (
  userType: string,
  hasPermission: (permission: string) => boolean,
  canSeeFunctions: boolean,
  setLayoutProps: (newProps: Partial<IAppLayoutProps>) => void,
  openDropdown?: (e: React.MouseEvent<HTMLElement>, type: DropdownType) => void
): INavLink[] => {
  const persistedParameters = [...Object.values(sitesAndLnsFilters)].filter((x) => x !== sitesAndLnsFilters.tab);

  const newLeaksPersistedParameters = Object.values(newLeaksFilters);
  const maintenancePersistedParameters = Object.values(maintenanceFilters);

  return [
    {
      label: <Trans>Dashboard</Trans>,
      path: AppPaths.statistics.path.base.path,
    },
    {
      label: <Trans>New&nbsp;Leaks</Trans>,
      path: AppPaths.newLeaks.path.base.path,
      parametersToKeep: newLeaksPersistedParameters,
      customColor: styleConstants.colors.charts.devices.leak,
      featureFlag: {
        flag: AppFeatureFlags.NewLeaks,
        modalTitle: <Trans>New Leaks</Trans>,
        info: {
          title: <Trans>TITLE_NEW_LEAKS_PREMIUM</Trans>,
          description: <Trans>DESCRIPTION_NEW_LEAKS_PREMIUM</Trans>,
          readMoreAtLink: 'https://www.hwmglobal.com/news/2023/06/29/new-leaks-dashboard-added-to-permanet-web/',
          footer: <Trans>FOOTER_PREMIUM</Trans>,
        },
      },
    },
    {
      label: <Trans>Map</Trans>,
      path: AppPaths.sitesMap.path.withAccountSider.path,
      parametersToKeep: persistedParameters,
      onClick: () => {
        setLayoutProps({ siderExpanded: true });
      },
    },
    {
      label: <Trans>Sites</Trans>,
      path: AppPaths.sites.path.withAccountSider.path,
      parametersToKeep: persistedParameters,
      onClick: () => {
        setLayoutProps({ siderExpanded: true });
      },
    },
    {
      label: <Trans>Surveys</Trans>,
      path: AppPaths.surveys.path.withAccountSider.path,
      featureFlag: {
        flag: AppFeatureFlags.MicrostreamSurveys,
        modalTitle: <></>,
        info: {
          title: <></>,
          description: <></>,
          footer: <></>,
        },
      },
    },
    {
      label: <Trans>LNS</Trans>,
      path: AppPaths.lns.path.base.path,
      parametersToKeep: persistedParameters,
    },
    {
      label: <Trans>Correlations</Trans>,
      path: AppPaths.correlations.path.base.path,
    },
    {
      label: <Trans>Audio&nbsp;Analysis</Trans>,
      path: AppPaths.audioAnalysisSummary.path.base.path,
      featureFlag: {
        flag: AppFeatureFlags.MLAudioAnalysis,
        modalTitle: <Trans>Audio Analysis</Trans>,
        info: {
          title: <Trans>TITLE_AUDIO_ANALYSIS_ML_PREMIUM</Trans>,
          description: <Trans>DESCRIPTION_AUDIO_ANALYSIS_ML_PREMIUM</Trans>,
          footer: <Trans>FOOTER_PREMIUM</Trans>,
        },
        videos: [],
      },
    },
    {
      label: <Trans>Leak&nbsp;Management</Trans>,
      path: '',
      absolutePath: true,
      onClick: (event) => {
        event.preventDefault();
        openDropdown(event, Dropdown.LeakManagement);
      },
    },
    ...(hasPermission(AppPermissions.CanSeeMaintenanceTab)
      ? [
          {
            label: <Trans>Maintenance</Trans>,
            path: AppPaths.maintenance.path.base.path,
            parametersToKeep: maintenancePersistedParameters,
          },
        ]
      : []),
    ...(userType === 'PCorr'
      ? [
          {
            label: <Trans>Deployments</Trans>,
            path: AppPaths.deployments.path.base.path,
          },
        ]
      : []),
    ...(canSeeFunctions
      ? [
          {
            label: <Trans>Functions</Trans>,
            path: '',
            absolutePath: true,
            onClick: (event) => {
              event.preventDefault();
              openDropdown(event, Dropdown.Functions);
            },
          } as INavLink,
        ]
      : []),
    {
      label: <Trans>Settings</Trans>,
      path: AppPaths.settings.path.base.path,
    },
    ...(hasPermission(AppPermissions.CanSeeImports)
      ? [
          {
            label: <Trans>GIS&nbsp;Management</Trans>,
            absolutePath: true,
            path: `${environment.gisManagementUrl}/advanced/`,
            onClick: (event) => {
              event.preventDefault();
              window.open(`${environment.gisManagementUrl}/advanced/${encodeURIComponent(window.location.href)}`);
            },
          } as INavLink,
        ]
      : []),
  ] as INavLink[];
};

const getMenuForLeakManagement = (): INavLink[] => {
  const leakAnalysisPersistedParameters = Object.values(leakAnalysisFilters);

  return [
    {
      label: <Trans>Leak Analysis</Trans>,
      path: AppPaths.leakAnalysis.path.base.path,
      parametersToKeep: leakAnalysisPersistedParameters,
      featureFlag: {
        flag: AppFeatureFlags.LeakAnalysis,
        modalTitle: <Trans>Leak Analysis</Trans>,
        info: {
          title: <Trans>TITLE_LEAK_ANALYSIS_PREMIUM</Trans>,
          description: <Trans>DESCRIPTION_LEAK_ANALYSIS_PREMIUM</Trans>,
          footer: <Trans>FOOTER_PREMIUM</Trans>,
        },
        videos: [],
      },
    },
    {
      label: <Trans>Leak Records</Trans>,
      path: AppPaths.leaks.path.base.path,
    },
  ];
};

const getMenuForFunctions = (hasPermission: (permission: string) => boolean): INavLink[] => {
  return [
    ...(hasPermission(AppPermissions.CanEditSites)
      ? [
          {
            label: <Trans>Upload Site Notes</Trans>,
            path: AppPaths.functions.path.uploadSiteNotes.path,
            featureFlag: {
              flag: AppFeatureFlags.SiteNoteBulkUpload,
              modalTitle: <Trans>Site Note Upload</Trans>,
              info: {
                title: <Trans>TITLE_SITE_NOTE_UPLOAD_PREMIUM</Trans>,
                description: <Trans>DESCRIPTION_SITE_NOTE_UPLOAD_PREMIUM</Trans>,
                footer: <Trans>FOOTER_PREMIUM</Trans>,
              },
            },
          },
        ]
      : []),
    ...(hasPermission(AppPermissions.CanLinkAccounts)
      ? [
          {
            label: <Trans>Linked PMZones</Trans>,
            path: AppPaths.functions.path.linkedPMZones.path,
          },
        ]
      : []),
    ...(hasPermission(AppPermissions.CanUseFunctionsBulkActions)
      ? [
          {
            label: <Trans>Bulk Actions</Trans>,
            path: AppPaths.functions.path.bulkActions.path,
          },
        ]
      : []),
  ];
};

//

const ITEM_LABEL_PADDING = 16;
const NAV_MENU_ITEM_HEIGHT = 32;

const useStyles = makeStyles(() => ({
  navbarLink: {
    width: '100%',
    height: NAV_MENU_ITEM_HEIGHT,
    color: styleConstants.colors.grey,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '16px',
    padding: `0 ${ITEM_LABEL_PADDING}px`,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
    },
    '&.active': {
      color: styleConstants.colors.blue,
      fontWeight: 700,
      pointerEvents: 'none',
    },
  },
}));

export const useGetLinks = () => {
  const classes = useStyles();

  const location = useLocation();
  const { hasPermission } = usePermission();
  const { hasFeatureFlag } = useFeatureFlag();
  const [setLayoutProps] = useSetLayoutProps<IAppLayoutProps>();

  const userType: string = useAppSelector(selectUserProfileType);

  const [dropdownMenuState, setDropdownMenuState] = useState<{
    anchor: HTMLElement;
    type: DropdownType;
  }>({ anchor: null, type: null });

  const canSeeFunctions = useMemo(
    () =>
      (hasPermission(AppPermissions.CanEditSites) && hasFeatureFlag(AppFeatureFlags.SiteNoteBulkUpload)) ||
      hasPermission(AppPermissions.CanLinkAccounts) ||
      hasPermission(AppPermissions.CanUseFunctionsBulkActions),
    [hasPermission, hasFeatureFlag]
  );

  const getMenuLinks = useCallback(() => {
    return getMenu(userType, hasPermission, canSeeFunctions, setLayoutProps, (e, type) =>
      setDropdownMenuState({ anchor: e.currentTarget, type })
    );
  }, [userType, canSeeFunctions, hasPermission, setLayoutProps]);

  const getSubMenuLinks = useCallback(
    (links: INavLink[]) => {
      return links.map((link, i) => {
        const component = (
          <NavLink
            key={link.path + i}
            to={{
              pathname: link.path,
              search: searchParams(link, location.search),
            }}
            className={classes.navbarLink}
            style={{
              ...(link?.customColor ? { color: link.customColor } : {}),
            }}
          >
            {link.label}
          </NavLink>
        );

        return (
          <div className="flex" key={link.path + i}>
            {isNil(link?.featureFlag) ? (
              <div className="p-1.5">{component}</div>
            ) : (
              <FeatureFlagInfoWrapper
                flag={link.featureFlag.flag}
                onlyFrame
                modalTitle={link.featureFlag.modalTitle}
                info={link.featureFlag.info}
                photos={link.featureFlag?.photos ?? []}
                videos={link.featureFlag?.videos ?? []}
              >
                {({ enabled }) => (enabled ? component : <div className={classes.navbarLink}>{link.label}</div>)}
              </FeatureFlagInfoWrapper>
            )}
          </div>
        );
      });
    },
    [location?.search, classes.navbarLink]
  );

  const dropdownMenuLinks: Record<DropdownType, JSX.Element[]> = useMemo(() => {
    const menuForFunctions = getMenuForFunctions(hasPermission);
    const menuForLeakManagement = getMenuForLeakManagement();

    return {
      [Dropdown.Functions]: getSubMenuLinks(menuForFunctions),
      [Dropdown.LeakManagement]: getSubMenuLinks(menuForLeakManagement),
    };
  }, [hasPermission, getSubMenuLinks]);

  return {
    getMenu: getMenuLinks,
    menuComponent: (
      <Menu
        style={{ zIndex: 10000 }}
        anchorEl={dropdownMenuState?.anchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        getContentAnchorEl={null}
        keepMounted
        open={Boolean(dropdownMenuState?.anchor)}
        onClick={() => setDropdownMenuState((prev) => ({ ...prev, anchor: null }))}
        classes={{
          list: 'p-2',
          paper: 'top-16',
        }}
      >
        <div className="flex flex-col items-center">{dropdownMenuLinks[dropdownMenuState.type] ?? null}</div>
      </Menu>
    ),
  };
};

//

const searchParams = (link: INavLink, locationSearch: string) => {
  const search = link.parametersToKeep?.length
    ? locationSearch
        .replace('?', '')
        .split('&')
        .filter((x) => link.parametersToKeep.includes(x.split('=')[0]))
        .join('&')
    : '';
  return search.length ? `?${search}` : '';
};
